import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SLIDES, type Slide } from './slidesData';
import ActionLinkButton from '../../UI/ActionLinkButton/ActionLinkButton';
import { YM_ID } from '../../metrics/ym';
import './bannerCarousel.scss';

const DISPLAY_DURATION_MS = 7000;
const TRANSITION_DURATION_MS = 1000;
// const MOBILE_BREAKPOINT = 768;

type BannerCarouselProps = {
    isMobile?: boolean;
};

/** Проверка високосного года */
const isLeapYear = (y: number): boolean => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
/** Кол-во дней в месяце */
const daysInMonth = (y: number, m: number): number => {
    const base = [31, isLeapYear(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return base[m - 1] ?? 0;
};
/** Парсим DD.MM.YYYY и валидируем существование даты. Возвращаем ключ YYYYMMDD или null */
const parseRuDateToKey = (value: string): number | null => {
    const v = value.trim();
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(v);
    if (!m) return null;
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);
    if (!Number.isInteger(dd) || !Number.isInteger(mm) || !Number.isInteger(yyyy)) return null;
    if (yyyy < 1900 || yyyy > 3000) return null;
    if (mm < 1 || mm > 12) return null;
    const dim = daysInMonth(yyyy, mm);
    if (dd < 1 || dd > dim) return null;
    return yyyy * 10000 + mm * 100 + dd;
};
/** Текущая дата по МСК как ключ YYYYMMDD. Если не получилось - null (и тогда ничего не скрываем) */
const getMoscowTodayKey = (): number | null => {
    try {
        const parts = new Intl.DateTimeFormat('ru-RU', {
            timeZone: 'Europe/Moscow',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(new Date());
        const y = Number(parts.find((p) => p.type === 'year')?.value);
        const m = Number(parts.find((p) => p.type === 'month')?.value);
        const d = Number(parts.find((p) => p.type === 'day')?.value);
        if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
        return y * 10000 + m * 100 + d;
    } catch {
        return null;
    }
};
/** Правило видимости: нет даты/NULL/плохая дата/не смогли получить МСК - показываем. Иначе показываем до даты включительно */
const isSlideVisible = (slide: Slide, nowMoscowKey: number | null): boolean => {
    const until = slide.visibleUntil;
    if (until == null || until === '') return true;
    if (nowMoscowKey == null) return true;
    const untilKey = parseRuDateToKey(until);
    if (untilKey == null) return true;
    return nowMoscowKey <= untilKey;
};

const BannerCarousel: React.FC<BannerCarouselProps> = ({ isMobile }) => {
    const { url } = useParams();
    const isDiscountsRoute = url === 'discounts';
    const [index, setIndex] = useState(1);
    const nowMoscowKey = getMoscowTodayKey();
    const dateFilteredSlides = SLIDES.filter((slide) => isSlideVisible(slide, nowMoscowKey));
    const filteredSlides = isDiscountsRoute ? dateFilteredSlides.filter((slide) => slide.type === 'promo') : dateFilteredSlides;
    const defSlides = isDiscountsRoute ? dateFilteredSlides.filter((slide) => slide.type === 'regular') : dateFilteredSlides;
    const activeSlides = filteredSlides.length > 0 ? filteredSlides : defSlides;
    const total = activeSlides.length;
    const extendedSlides = total > 0 ? [activeSlides[total - 1], ...activeSlides, activeSlides[0]] : [];
    const slideCount = extendedSlides.length || 1;
    const slideWidthPercent = total > 0 ? 100 / slideCount : 100;
    const realIndex = total > 0 ? (index - 1 + total) % total : 0;
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    // const [isMobile, setIsMobile] = useState(window.matchMedia(`(max-width:${MOBILE_BREAKPOINT}px)`).matches);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const timeoutRef = useRef<number | null>(null);
    const transitionRef = useRef<number | null>(null);
    const progressKeyRef = useRef<number>(0);
    const slideStartRef = useRef<number>(performance.now());
    const remainingRef = useRef<number>(DISPLAY_DURATION_MS);
    const scheduledEndRef = useRef<number>(slideStartRef.current + DISPLAY_DURATION_MS);
    const startXRef = useRef(0);
    const startYRef = useRef(0);
    const lastXRef = useRef(0);
    const dragStartTimeRef = useRef(0);
    const isDraggingRef = useRef(false);
    const lockedAxisRef = useRef<'x' | 'y' | null>(null);
    const viewportWidthRef = useRef(0);

    // useEffect(() => {
    //     const mq = window.matchMedia(`(max-width:${MOBILE_BREAKPOINT}px)`);
    //     const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    //     if (mq.addEventListener) mq.addEventListener('change', handler);
    //     else mq.addListener(handler);
    //     return () => {
    //         if (mq.removeEventListener) mq.removeEventListener('change', handler);
    //         else mq.removeListener(handler);
    //     };
    // }, []);

    const clearDisplayTimeout = () => {
        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const scheduleAdvance = (delay: number) => {
        clearDisplayTimeout();
        scheduledEndRef.current = performance.now() + delay;
        remainingRef.current = delay;
        timeoutRef.current = window.setTimeout(() => {
            startTransition();
        }, delay);
    };

    const startTransition = () => {
        setIsTransitioning(true);
        setIndex((prev) => prev + 1);
    };

    useEffect(() => {
        if (total <= 1) return;
        if (isTransitioning) return;
        slideStartRef.current = performance.now();
        remainingRef.current = DISPLAY_DURATION_MS;
        scheduledEndRef.current = slideStartRef.current + DISPLAY_DURATION_MS;
        progressKeyRef.current += 1;
        scheduleAdvance(DISPLAY_DURATION_MS);
        return () => {
            clearDisplayTimeout();
        };
        // eslint-disable-next-line
    }, [isTransitioning, total]);

    const handleProgressClick = (target: number) => {
        if (target === realIndex) return;
        if (isTransitioning) {
            if (transitionRef.current) window.clearTimeout(transitionRef.current);
        }
        clearDisplayTimeout();
        setIsTransitioning(true);
        setIndex(target + 1);
    };

    useEffect(() => {
        if (total <= 1) return;
        if (!isTransitioning) return;
        const handle = window.setTimeout(() => {
            setIsTransitioning(false);
            if (index === total + 1) {
                setIndex(1);
                if (containerRef.current) {
                    containerRef.current.style.transition = 'none';
                    const shiftPercent = -(1 * slideWidthPercent);
                    containerRef.current.style.transform = `translateX(${shiftPercent}%)`;
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    containerRef.current.offsetHeight;
                    requestAnimationFrame(() => {
                        if (containerRef.current) {
                            containerRef.current.style.transition = `transform ${TRANSITION_DURATION_MS}ms ease`;
                        }
                    });
                }
            }
            if (index === 0) {
                setIndex(total);
                if (containerRef.current) {
                    containerRef.current.style.transition = 'none';
                    const shiftPercent = -(total * slideWidthPercent);
                    containerRef.current.style.transform = `translateX(${shiftPercent}%)`;
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    containerRef.current.offsetHeight;
                    requestAnimationFrame(() => {
                        if (containerRef.current) {
                            containerRef.current.style.transition = `transform ${TRANSITION_DURATION_MS}ms ease`;
                        }
                    });
                }
            }
        }, TRANSITION_DURATION_MS);
        transitionRef.current = handle;
        return () => {
            if (transitionRef.current) window.clearTimeout(transitionRef.current);
        };
    }, [index, total, isTransitioning, slideWidthPercent]);

    useEffect(() => {
        if (containerRef.current) {
            if (!isTransitioning) {
                containerRef.current.style.transition = `transform ${TRANSITION_DURATION_MS}ms ease`;
            }
            const effectiveIndex = index;
            const shiftPercent = -(effectiveIndex * slideWidthPercent);
            containerRef.current.style.transform = `translateX(${shiftPercent}%)`;
        }
    }, [index, isTransitioning, slideCount, slideWidthPercent]);

    useEffect(() => {
        return () => {
            clearDisplayTimeout();
            if (transitionRef.current) window.clearTimeout(transitionRef.current);
        };
    }, []);

    const handleMouseEnter = () => {
        if (isTransitioning || isPaused) return;
        const now = performance.now();
        const remaining = Math.max(0, scheduledEndRef.current - now);
        if (remaining < DISPLAY_DURATION_MS) {
            remainingRef.current = remaining;
        }
        clearDisplayTimeout();
        setIsPaused(true);
    };

    const handleMouseLeave = () => {
        if (isTransitioning) return;
        if (!isPaused) return;
        const toSchedule = Math.max(0, remainingRef.current);
        slideStartRef.current = performance.now();
        scheduledEndRef.current = slideStartRef.current + toSchedule;
        scheduleAdvance(toSchedule);
        setIsPaused(false);
    };

    const applyDragTransform = (dx: number) => {
        if (!containerRef.current) return;
        const slidePercent = slideWidthPercent;
        const vw = viewportWidthRef.current || (viewportRef.current?.clientWidth ?? window.innerWidth);
        const deltaPercent = (dx / vw) * slidePercent;
        const basePercent = -(index * slidePercent);
        containerRef.current.style.transform = `translateX(${basePercent + deltaPercent}%)`;
    };

    const onTouchStart = (e: React.TouchEvent) => {
        if (!isMobile || isTransitioning) return;
        const t = e.touches[0];
        startXRef.current = t.clientX;
        startYRef.current = t.clientY;
        lastXRef.current = t.clientX;
        dragStartTimeRef.current = performance.now();
        isDraggingRef.current = true;
        lockedAxisRef.current = null;
        viewportWidthRef.current = viewportRef.current?.clientWidth ?? window.innerWidth;
        clearDisplayTimeout();
        setIsPaused(true);
        if (containerRef.current) {
            containerRef.current.style.transition = 'none';
        }
    };

    const onTouchMove = (e: React.TouchEvent) => {
        if (!isMobile || !isDraggingRef.current || !containerRef.current) return;
        const t = e.touches[0];
        const dx = t.clientX - startXRef.current;
        const dy = t.clientY - startYRef.current;
        if (lockedAxisRef.current === null) {
            if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
                lockedAxisRef.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
            }
        }
        if (lockedAxisRef.current === 'y') {
            return;
        }
        lastXRef.current = t.clientX;
        applyDragTransform(dx);
    };

    const snapToIndex = (idx: number) => {
        if (!containerRef.current) return;
        containerRef.current.style.transition = `transform ${TRANSITION_DURATION_MS}ms ease`;
        const slidePercent = slideWidthPercent;
        const shiftPercent = -(idx * slidePercent);
        containerRef.current.style.transform = `translateX(${shiftPercent}%)`;
    };

    const onTouchEnd = () => {
        if (!isMobile || !isDraggingRef.current) return;
        isDraggingRef.current = false;
        const dx = lastXRef.current - startXRef.current;
        const dt = performance.now() - dragStartTimeRef.current;
        const distance = Math.abs(dx);
        const vw = viewportWidthRef.current || (viewportRef.current?.clientWidth ?? window.innerWidth);
        const distanceThreshold = Math.min(80, vw * 0.12);
        const velocity = distance / Math.max(dt, 1);
        const velocityThreshold = 0.5;
        if (containerRef.current) {
            containerRef.current.style.transition = `transform ${TRANSITION_DURATION_MS}ms ease`;
        }
        let advanced = false;
        if (distance > distanceThreshold || velocity > velocityThreshold) {
            if (dx < 0) {
                setIsTransitioning(true);
                setIndex((prev) => prev + 1);
                advanced = true;
            } else if (dx > 0) {
                setIsTransitioning(true);
                setIndex((prev) => prev - 1);
                advanced = true;
            }
        }
        if (!advanced) {
            snapToIndex(index);
        }
        setIsPaused(false);
        const toSchedule = Math.max(0, remainingRef.current);
        slideStartRef.current = performance.now();
        scheduledEndRef.current = slideStartRef.current + toSchedule;
        scheduleAdvance(toSchedule);
    };

    const handlePromoButtonClick = (e: React.MouseEvent<HTMLButtonElement>, slide: Slide) => {
        if (!slide.goalId) return;
        if (process.env.NODE_ENV !== 'production') return;
        const counterId = YM_ID || 103250323;
        if (!counterId) return;
        e.preventDefault();
        e.stopPropagation();
        let navigated = false;
        const navigate = () => {
            if (navigated) return;
            navigated = true;
            window.location.assign(slide.linkPath);
        };
        try {
            window.ym?.(counterId, 'reachGoal', slide.goalId, {}, navigate);
        } catch {
            // ignore
        }
        window.setTimeout(navigate, 900);
    };

    if (total === 0) {
        return null;
    }

    return (
        <div className="bannerCarousel">
            <div
                className={`bannerCarousel__viewport ${isDiscountsRoute ? 'bannerCarousel__viewport_showcaseDiscounts' : ''}`}
                ref={(el) => {
                    viewportRef.current = el;
                }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                <div
                    className="bannerCarousel__track"
                    ref={(el) => {
                        containerRef.current = el;
                    }}
                    style={{
                        transition: `transform ${TRANSITION_DURATION_MS}ms ease`,
                        width: `${extendedSlides.length * 100}%`,
                        transform: `translateX(${-(index * slideWidthPercent)}%)`,
                    }}
                    aria-live="off"
                >
                    {isMobile
                        ? extendedSlides.map((slide, i) => {
                              const imageSrc = isMobile && slide.imageMobile ? slide.imageMobile : slide.image;
                              const slideBaseClass = slide.class;
                              const slideClassName = ['bannerCarousel__slide', slideBaseClass].filter(Boolean).join(' ');
                              const bgClassName = ['bannerCarousel__bg', slideBaseClass ? `${slideBaseClass}__bg` : ''].filter(Boolean).join(' ');
                              const textClassName = [
                                  'bannerCarousel__text',
                                  slideBaseClass ? `${slideBaseClass}__text` : '',
                                  slideBaseClass === 'mobFirstSlide' ? 'mobFirstSlide' : '',
                                  `pos-horizontal-${slide.textPosition.horizontal}`,
                                  `pos-vertical-${slide.textPosition.vertical}`,
                              ]
                                  .filter(Boolean)
                                  .join(' ');
                              const pretitleClassName = ['bannerCarousel__pretitle', slideBaseClass ? `${slideBaseClass}__pretitle` : ''].filter(Boolean).join(' ');
                              const titleClassName = ['bannerCarousel__title', slideBaseClass ? `${slideBaseClass}__title` : ''].filter(Boolean).join(' ');
                              const subtitleClassName = ['bannerCarousel__subtitle', slideBaseClass ? `${slideBaseClass}__subtitle` : ''].filter(Boolean).join(' ');
                              const discountClassName = ['bannerCarousel__discount', slideBaseClass ? `${slideBaseClass}__discount` : ''].filter(Boolean).join(' ');
                              const discountValueClassName = ['bannerCarousel__discount-value', slideBaseClass ? `${slideBaseClass}__discount-value` : '']
                                  .filter(Boolean)
                                  .join(' ');
                              const discountUnitClassName = ['bannerCarousel__discount-unit', slideBaseClass ? `${slideBaseClass}__discount-unit` : '']
                                  .filter(Boolean)
                                  .join(' ');
                              const discountTextClassName = ['bannerCarousel__discount-text', slideBaseClass ? `${slideBaseClass}__discount-text` : '']
                                  .filter(Boolean)
                                  .join(' ');
                              const linkClassName = ['bannerCarousel__link', slideBaseClass ? `${slideBaseClass}__link` : ''].filter(Boolean).join(' ');

                              return (
                                  <div className={slideClassName} key={`slide-mob-${i}`} style={{ flex: `0 0 ${slideWidthPercent}%` }}>
                                      <div className={bgClassName} style={{ backgroundImage: `url(${imageSrc})` }} aria-label={slide.title || slide.pretitle} />
                                      <a href={slide.linkPath} className={textClassName} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                                          {slide.pretitle && <p className={pretitleClassName}>{slide.pretitle}</p>}
                                          {slide.title && <h2 className={titleClassName}>{slide.title}</h2>}
                                          {slide.discount && (
                                              <div className={discountClassName}>
                                                  <span className={discountValueClassName}>{slide.discount.value}</span>
                                                  <span className={discountUnitClassName}>{slide.discount.unit}</span>
                                                  <span className={discountTextClassName}>{slide.discount.text}</span>
                                              </div>
                                          )}
                                          {slide.subtitle && <p className={subtitleClassName}>{slide.subtitle}</p>}
                                          {slide.linkText && (
                                              <button className={linkClassName} onClick={(e) => handlePromoButtonClick(e, slide)}>
                                                  {slide.linkText}
                                              </button>
                                          )}
                                      </a>
                                  </div>
                              );
                          })
                        : extendedSlides.map((slide, i) => {
                              const imageSrc = isMobile && slide.imageMobile ? slide.imageMobile : slide.image;
                              const slideBaseClass = slide.class;
                              const slideClassName = ['bannerCarousel__slide', slideBaseClass].filter(Boolean).join(' ');
                              const bgClassName = ['bannerCarousel__bg', slideBaseClass ? `${slideBaseClass}__bg` : ''].filter(Boolean).join(' ');
                              const textClassName = [
                                  'bannerCarousel__text',
                                  slideBaseClass ? `${slideBaseClass}__text` : '',
                                  slideBaseClass === 'mobFirstSlide' ? 'mobFirstSlide' : '',
                                  `pos-horizontal-${slide.textPosition.horizontal}`,
                                  `pos-vertical-${slide.textPosition.vertical}`,
                              ]
                                  .filter(Boolean)
                                  .join(' ');
                              const pretitleClassName = ['bannerCarousel__pretitle', slideBaseClass ? `${slideBaseClass}__pretitle` : ''].filter(Boolean).join(' ');
                              const titleClassName = ['bannerCarousel__title', slideBaseClass ? `${slideBaseClass}__title` : ''].filter(Boolean).join(' ');
                              const subtitleClassName = ['bannerCarousel__subtitle', slideBaseClass ? `${slideBaseClass}__subtitle` : ''].filter(Boolean).join(' ');
                              const discountClassName = ['bannerCarousel__discount', slideBaseClass ? `${slideBaseClass}__discount` : ''].filter(Boolean).join(' ');
                              const discountValueClassName = ['bannerCarousel__discount-value', slideBaseClass ? `${slideBaseClass}__discount-value` : '']
                                  .filter(Boolean)
                                  .join(' ');
                              const discountUnitClassName = ['bannerCarousel__discount-unit', slideBaseClass ? `${slideBaseClass}__discount-unit` : '']
                                  .filter(Boolean)
                                  .join(' ');
                              const discountTextClassName = ['bannerCarousel__discount-text', slideBaseClass ? `${slideBaseClass}__discount-text` : '']
                                  .filter(Boolean)
                                  .join(' ');
                              const linkClassName = ['bannerCarousel__link', slideBaseClass ? `${slideBaseClass}__link` : ''].filter(Boolean).join(' ');

                              return (
                                  <div className={slideClassName} key={`slide-${i}`} style={{ flex: `0 0 ${slideWidthPercent}%` }}>
                                      <div className={bgClassName} style={{ backgroundImage: `url(${imageSrc})` }} aria-label={slide.title || slide.pretitle} />
                                      <div className="bannerCarousel__content">
                                          <a href={slide.linkPath} className={textClassName} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                                              {slide.pretitle && <p className={pretitleClassName}>{slide.pretitle}</p>}
                                              {slide.title && <h2 className={titleClassName}>{slide.title}</h2>}
                                              {slide.discount && (
                                                  <div className={discountClassName}>
                                                      <span className={discountValueClassName}>{slide.discount.value}</span>
                                                      <span className={discountUnitClassName}>{slide.discount.unit}</span>
                                                      <span className={discountTextClassName}>{slide.discount.text}</span>
                                                  </div>
                                              )}
                                              {slide.subtitle && <p className={subtitleClassName}>{slide.subtitle}</p>}
                                              {slide.linkText && (
                                                  <button className={linkClassName} onClick={(e) => handlePromoButtonClick(e, slide)}>
                                                      {slide.linkText}
                                                  </button>
                                              )}
                                          </a>
                                      </div>
                                  </div>
                              );
                          })}
                </div>
                {activeSlides && activeSlides.length > 1 && (
                    <div style={isMobile ? { display: 'flex', justifyContent: 'center' } : undefined} className="bannerCarousel__progress">
                        <ActionLinkButton
                            ariaLabel="Предыдущий слайд"
                            iconAlt="Предыдущий слайд"
                            iconSrc="/icons/mobile/chevron-left.svg"
                            iconSrcDark="/icons/mobile/chevron-left_w.svg"
                            className="bannerCarousel__progress_btn"
                            onClick={() => handleProgressClick(realIndex - 1)}
                        />
                        {activeSlides.map((_, i) => (
                            <div
                                className="bannerCarousel__progress-bar-wrapper"
                                key={i}
                                role="button"
                                aria-label={`Перейти к баннеру ${i + 1}`}
                                tabIndex={0}
                                onClick={() => handleProgressClick(i)}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleProgressClick(i);
                                    }
                                }}
                            >
                                <div
                                    className={`bannerCarousel__progress-bar ${realIndex === i ? 'active' : ''}`}
                                    key={`progress-${i}-${realIndex === i ? progressKeyRef.current : 0}`}
                                    style={
                                        realIndex === i
                                            ? {
                                                  animationDuration: `${DISPLAY_DURATION_MS}ms`,
                                                  animationPlayState: isPaused ? 'paused' : 'running',
                                              }
                                            : {}
                                    }
                                />
                            </div>
                        ))}
                        <ActionLinkButton
                            ariaLabel="Следующий сладйд"
                            iconAlt="Следующий сладйд"
                            iconSrc="/icons/mobile/chevron-right.svg"
                            iconSrcDark="/icons/mobile/chevron-right_w.svg"
                            className="bannerCarousel__progress_btn"
                            onClick={() => handleProgressClick(realIndex + 1)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default BannerCarousel;
